// src/ListPage.tsx
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import './userList.scss';
import { RootState } from '../../store/store';
import { userService } from '../../services/apiService';
import { User as UserApi } from '../../store/interfaces';
import UserForm from './userForm';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

export type User = {
  id?: number;
  name: string;
  email: string;
  role: 'USER' | 'ADMIN';
  password: string;
};

const UserPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const [users, setUsers] = React.useState<UserApi[]>([]);
  const [loading, setLoading] = React.useState(false);
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      if (!user) {
        return;
      }

      setLoading(true);
      try {
        const data = await userService.getUsers(user);
        setUsers(data);
        console.log('Get Users successful', data);
      } catch (error) {
        console.error('Get Users error:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);

  if (loading) {
    return <p>Fetching users...</p>;
  }

  const submitForm = async (formData: User) => {
    if (!user) {
      return;
    }

    try {
      let response;
      if (params.id === '0') {
        // Create new user - convert role to API format
        const apiRole = formData.role === 'ADMIN' ? 'ADMIN' : 'USER';

        const createUserData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.name.split(' ')[0] || '',
          lastName: formData.name.split(' ').slice(1).join(' ') || '',
          role: apiRole as 'USER' | 'ADMIN' | 'SUPERADMIN',
        };
        response = await userService.createUser(user, createUserData);

        // Convert CreateUserResponse to UserApi for state update
        const userApi: UserApi = {
          id: response.id,
          email: response.email,
          firstName: response.firstName,
          lastName: response.lastName,
          role: response.role,
          companyId: response.companyId,
          createdAt: response.createdAt,
          updatedAt: null,
        };

        setUsers([...users, userApi]);
      } else {
        // Update existing user - Note: API doesn't have update user endpoint
        // You might need to implement this based on your API
        console.warn('Update user not implemented in API');
        return;
      }

      navigate('/users');
    } catch (error) {
      console.error('Save User Failed:', error);
    }
  };

  const viewUsers = (
    <ul className="user-list">
      <li key="-1" className={clsx('user-item', 'user-header')}>
        <div className="user-name">Name</div>
        <div className="user-employees">Role</div>
        <div className="user-location">Edit</div>
      </li>
      {users.map((user, index) => (
        <li key={index} className="user-item">
          <div className="user-name">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName || user.lastName || 'No Name'}</div>
          <div className="user-role">{user.role}</div>
          <div className="user-edit">
            <Link to={`/users/${user.id}`}>
              <button>Edit</button>
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div className="user-container">
        <div className={'user-topline'}>
          <h2>Users</h2>
          <Link to={`/users/0`}>
            <button>Add New</button>
          </Link>
        </div>
        {Number(params.id) >= 0 ? (
          <UserForm
            onSubmit={submitForm}
            initialData={users.find((c) => c.id === Number(params.id)) ? {
              id: users.find((c) => c.id === Number(params.id))?.id,
              name: users.find((c) => c.id === Number(params.id))?.firstName && users.find((c) => c.id === Number(params.id))?.lastName
                ? `${users.find((c) => c.id === Number(params.id))?.firstName} ${users.find((c) => c.id === Number(params.id))?.lastName}`
                : users.find((c) => c.id === Number(params.id))?.firstName || users.find((c) => c.id === Number(params.id))?.lastName || '',
              email: users.find((c) => c.id === Number(params.id))?.email || '',
              role: (users.find((c) => c.id === Number(params.id))?.role === 'ADMIN' ? 'ADMIN' : 'USER') as 'USER' | 'ADMIN',
              password: '',
            } : undefined}
          />
        ) : (
          viewUsers
        )}
      </div>
    </>
  );
};

export default UserPage;
